if (!customElements.get("aisle-product-discovery")) {
  const AISLE_LOADER_SRC =
    "https://cdn.gotoaisle.com/product-discovery-v1/script/loader/productDiscoveryLoader.js";
  // The Aisle loader exposes setProductDiscoveryParentPageUrl asynchronously
  const SYNC_RETRY_INTERVAL = 250;
  const SYNC_RETRY_LIMIT = 60;
  const EMPTY_CHECK_DELAY = 200;

  let loaderPromise;
  const loadAisleLoader = () => {
    loaderPromise =
      loaderPromise ||
      new Promise((resolve, reject) => {
        if (window.aisleLoader) return resolve();
        const script = document.createElement("script");
        script.src = AISLE_LOADER_SRC;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    return loaderPromise;
  };

  customElements.define(
    "aisle-product-discovery",
    class AisleProductDiscovery extends HTMLElement {
      connectedCallback() {
        this.onVariantChange = this.onVariantChange.bind(this);
        document.addEventListener("variantChangeEnd", this.onVariantChange);

        const { widgetId } = this.dataset;
        const config = {
          widgetId,
          rootTargetElement: `aisle-product-discovery__${widgetId}`,
          isProductDiscovery: true,
        };
        window.aisleProductDiscoveryConfigurations =
          window.aisleProductDiscoveryConfigurations || {};
        window.aisleProductDiscoveryConfigurations[widgetId] = config;

        loadAisleLoader()
          .then(() => {
            window.aisleLoader.loadAssets(config);
            this.syncWhenReady();
            this.watchContent();
          })
          .catch((error) => console.error("Aisle loader failed", error));
      }

      disconnectedCallback() {
        document.removeEventListener("variantChangeEnd", this.onVariantChange);
        clearInterval(this.syncRetry);
        clearInterval(this.frameRetry);
        clearTimeout(this.emptyTimer);
        this.contentObserver?.disconnect();
      }

      // The widget renders into a srcdoc iframe (same origin, so readable).
      // Collapse the whole embed while the iframe has nothing to show, and
      // bring it back as soon as content appears (e.g. another variant).
      watchContent() {
        if (!this.hasAttribute("data-hide-when-empty")) return;
        let attempts = 0;
        this.frameRetry = setInterval(() => {
          attempts += 1;
          const frame = this.querySelector("#product-discovery-frame");
          const doc = frame?.contentDocument;
          if (doc?.body) {
            clearInterval(this.frameRetry);
            this.contentObserver = new MutationObserver(() => this.checkContent(doc));
            this.contentObserver.observe(doc.body, {
              childList: true,
              subtree: true,
              characterData: true,
            });
            this.checkContent(doc);
          } else if (attempts >= SYNC_RETRY_LIMIT * 2) {
            clearInterval(this.frameRetry);
          }
        }, SYNC_RETRY_INTERVAL);
      }

      checkContent(doc) {
        const hasContent =
          doc.body.innerText.trim() !== "" ||
          doc.body.querySelector("img, button, a, input, select, svg, canvas");
        clearTimeout(this.emptyTimer);
        if (hasContent) {
          this.hidden = false;
        } else {
          // Wait for Aisle to finish loading before deciding it is empty
          this.emptyTimer = setTimeout(() => {
            this.hidden = true;
          }, EMPTY_CHECK_DELAY);
        }
      }

      // variantChangeEnd is dispatched by <variant-options> (assets/variants.js)
      onVariantChange(event) {
        const variantOptions = event.target;
        if (variantOptions?.dataset?.section !== this.dataset.section) return;
        if (!variantOptions.currentVariant) return;

        this.dataset.variantId = variantOptions.currentVariant.id;
        this.sync();
      }

      // Canonical variant URL (primary domain), matching the URLs mapped in Aisle
      get pageUrl() {
        const { productUrl, variantId } = this.dataset;
        return variantId ? `${productUrl}?variant=${variantId}` : productUrl;
      }

      sync() {
        if (typeof window.setProductDiscoveryParentPageUrl !== "function") {
          return false;
        }
        window.setProductDiscoveryParentPageUrl(this.pageUrl);
        return true;
      }

      syncWhenReady() {
        if (this.sync()) return;
        let attempts = 0;
        this.syncRetry = setInterval(() => {
          attempts += 1;
          if (this.sync() || attempts >= SYNC_RETRY_LIMIT) {
            clearInterval(this.syncRetry);
          }
        }, SYNC_RETRY_INTERVAL);
      }
    },
  );
}
